<script setup>
  import Offcanvas from 'bootstrap/js/dist/offcanvas';
  import {computed, nextTick, onMounted, onUnmounted, ref} from 'vue';

  const props = defineProps({
    dataForHelpSideDrawer: {
      type: Object,
      default: () => ({}),
    }
  });

  // const isChatboxOpen = ref(false);
  const offcanvasElement = ref(null);
  const offcanvasInstance = ref(null);
  const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const isShowingAllQuestions = ref(false);

  const phoneNumber = "2103245000";
  const telHref = computed(() => `tel:${phoneNumber}`);
  const isCompactCta = computed(() => viewportWidth.value <= 1199);
  const questions = computed(() => props.dataForHelpSideDrawer?.questions ?? []);

  const visibleQuestionLimit = computed(() => {
    if (viewportWidth.value <= 767) {
      return 3;
    }

    if (viewportWidth.value <= 1199) {
      return 4;
    }

    return 5;
  });

  const hasMoreQuestions = computed(() => questions.value.length > visibleQuestionLimit.value);
  const visibleQuestions = computed(() => {
    if (isShowingAllQuestions.value || !hasMoreQuestions.value) {
      return questions.value;
    }

    return questions.value.slice(0, visibleQuestionLimit.value);
  });

  const allQuestionsLabel = computed(() => `Δες όλες τις ερωτήσεις (${questions.value.length}+)`);

  const updateViewportWidth = () => {
    if (typeof window === 'undefined') {
      return;
    }

    viewportWidth.value = window.innerWidth;
  };

  const isMobileDevice = () => {
    if (typeof window === 'undefined') {
      return false;
    }

    const isPhoneWidth = window.matchMedia('(max-width: 767px)').matches;
    const isPhoneUA = /Android|iPhone|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return isPhoneWidth && isPhoneUA;
  };

  onMounted(async () => {
    await nextTick();

    if (offcanvasElement.value) {
      offcanvasInstance.value = Offcanvas.getOrCreateInstance(offcanvasElement.value);
    }

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateViewportWidth);
    offcanvasInstance.value?.dispose();
    offcanvasInstance.value = null;
  });

  defineExpose({
    openHelpDrawer: () => {
      isShowingAllQuestions.value = false;
      offcanvasInstance.value?.show();
    },
  });

  const callUsOnClick = async (event) => {
    if (isMobileDevice()) {
      return;
    }

    event?.preventDefault();

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(phoneNumber);
        return;
      } catch {
        // Clipboard write can fail. Keep dial flow as fallback.
      }
    }

    window.location.href = telHref.value;
  };

  // const openChatbox = () => {
  //   isChatboxOpen.value = true;
  // };

  // const closeChatbox = () => {
  //   isChatboxOpen.value = false;
  // };

  const showAllQuestions = () => {
    isShowingAllQuestions.value = true;
  };
</script>

<template>
  <Teleport to="body">
    <div
        ref="offcanvasElement"
        class="product__offcanvas offcanvas offcanvas-end"
        tabindex="-1"
        :id="`product-1`"
    >
      <div class="offcanvas-header d-flex title-main">
        <div class="header-content">
          <h2 class="fs-3 px-2 py-2">Χρειάζεσαι βοήθεια;</h2>
          <!-- <div class="pill-live">
            <slot>● Live</slot>
          </div> -->
        </div>

        <div class="close-btn" data-bs-dismiss="offcanvas">
          <vue-feather type="x" size="16" stroke-width="4"></vue-feather>
        </div>
      </div>

      <div class="body-container">
        <div class="cta-sticky">
          <a class="btn btn-call" :class="{ 'btn-call--compact': isCompactCta }" :href="telHref" @click="callUsOnClick($event)">
            <vue-feather
                class="call-icon"
                type="phone"
                stroke="#FFFFFF"
                size="20"
            ></vue-feather>

            <div class="text" :class="{ 'text--compact': isCompactCta }">
              <div class="btn-title">Κάλεσε μας</div>
              <div v-if="!isCompactCta" class="btn-subtitle">{{ phoneNumber }}</div>
            </div>
          </a>

          <!-- <button class="btn btn-chatbox" @click="openChatbox">
            <vue-feather
                class="arrow-icon"
                type="message-circle"
                stroke="#FFFFFF"
                size="20"
            ></vue-feather>

            <div class="text">
              <div class="btn-title">Live Chat</div>
              <div class="btn-subtitle">~30 δευτ.</div>
            </div>
          </button> -->
        </div>

        <div class="faq-heading py-4">
          <h2 class="fs-4 px-2 py-2">Συχνές ερωτήσεις</h2>
          <div class="pill-instant-answer">
            <slot>Βρες άμεσες απαντήσεις</slot>
          </div>
        </div>

        <div v-if="questions.length > 0">
          <div class="accordion accordion-flush" id="faqAccordion">
            <div v-for="(question, index) in visibleQuestions" :key="index" class="accordion-item faq">
              <div class="accordion-header">
                <div
                    class="px-4 py-3 cursor-pointer d-flex align-items-center collapsed"
                    data-bs-toggle="collapse"
                    :data-bs-target="`#faqCollapse-${index}`"
                    data-bs-parent="#faqAccordion"
                >
                  <div class="fs-5 fw-bold">{{ question.question }}</div>

                  <vue-feather
                      class="ms-auto faq-arrow"
                      type="chevron-down"
                      stroke="#1A1A1A"
                      size="20"
                  ></vue-feather>
                </div>
              </div>

              <div :id="`faqCollapse-${index}`" class="accordion-collapse collapse" :class="{ show: index === 0 }">
                <div class="accordion-body px-4">
                  <div class="d-flex flex-column gap-2">
                    <div class="d-flex justify-content-between">
                      <span class="text-n700 small m-0" v-html="question.answer"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="hasMoreQuestions && !isShowingAllQuestions" class="faq-footer">
            <button class="faq-footer-btn" type="button" @click="showAllQuestions">
              <span>{{ allQuestionsLabel }}</span>
              <vue-feather type="chevron-right" size="18" stroke-width="2.5"></vue-feather>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- <transition name="slide-fade">
      <div v-if="isChatboxOpen" class="chatbox-modal">
        <div class="chatbox-header">
          <h3>Live Chat</h3>
          <button class="close-btn" @click="closeChatbox">
            <vue-feather type="x" size="20" stroke="#FFFFFF"></vue-feather>
          </button>
        </div>
        <div class="chatbox-content">
          <iframe
              src="https://copilotstudio.microsoft.com/environments/f3316bba-d3f5-447e-9db9-0561742683ff/bots/cr15a_checkoutPal/webchat?__version__=2"
              frameborder="0"
              style="width: 100%; height: 100%; border: none;"></iframe>
        </div>
      </div>
    </transition> -->
  </Teleport>
</template>

<style scoped>
  .body-container {
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 30px 30px;
  }

  .title-main {
    padding: 20px 15px 11px 20px;
  }

  .header-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .pill-live {
    height: 22px;
    margin: 13px;
    padding: 0 8px;
    border-radius: 9999px;
    font-size: 12px;
    border: none;
    cursor: pointer;
    color: white;
    background: #259b69;
    transition: opacity 0.2s ease-in-out;
  }

  .cta-sticky {
    top: 0;
    z-index: 2;
    position: sticky;
    padding: 18px 0 16px;
    background: linear-gradient(180deg, #f8f6f4 0%, #f8f6f4 84%, rgba(248, 246, 244, 0) 100%);
  }

  .btn {
    border: none;
    border-radius: 16px;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
    box-shadow: 0 8px 20px rgba(36, 122, 118, 0.22);
  }

  .btn:hover {
    color: #ffffff !important;
  }

  .btn-call {
    width: min(100%, 215px);
    min-height: 54px;
    padding: 12px 18px;
    gap: 14px;
    background: linear-gradient(90deg, #4cb38a, #29adaa);
  }

  .call-icon {
    flex-shrink: 0;
  }

  .arrow-icon {
    flex-shrink: 0;
  }

  .text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    line-height: 1.15;
  }

  .btn-title {
    font-size: 1.05rem;
    font-weight: 800;
  }

  .btn-subtitle {
    font-size: 0.9rem;
  }

  .faq-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
  }

  .pill-instant-answer {
    height: 25px;
    margin: 10px 10px 10px 0;
    padding: 1px 11px;
    border-radius: 9999px;
    font-size: 12px;
    color: white;
    background: linear-gradient(90deg, #4cc5d9 0%, #32b3c3 30%, #9b1c96 100%);
  }

  .accordion {
    margin-bottom: 24px;
  }

  .faq {
    border-radius: 16px;
    background: #ffffff;
  }

  .faq-arrow {
    flex-shrink: 0;
  }

  .faq-footer {
    padding-bottom: 8px;
  }

  .faq-footer-btn {
    width: 100%;
    padding: 14px 24px;
    border: 1px solid #b2b0ad;
    border-radius: 9999px;
    color: #6b6763;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 1rem;
    font-weight: 500;
    background: #f6f6f6;
  }

  .btn-chatbox {
    background: linear-gradient(135deg, #29adab, #29adab);
  }

  .chatbox-modal {
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 400px;
    height: 600px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16);
    display: flex;
    flex-direction: column;
    z-index: 1050;
  }

  .chatbox-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: linear-gradient(135deg, #29adab, #29adab);
    border-radius: 12px 12px 0 0;
    color: white;
  }

  .chatbox-header h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .chatbox-header .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
  }

  .chatbox-header .close-btn:hover {
    opacity: 0.8;
  }

  .chatbox-content {
    flex: 1;
    overflow: hidden;
    border-radius: 0 0 12px 12px;
  }

  @media (max-width: 1199px) {
    .title-main {
      padding: 10px 8px 8px;
      align-items: flex-start;
    }

    .body-container {
      padding: 0 8px 20px;
    }

    .cta-sticky {
      padding: 10px 0 12px;
    }

    .btn-call {
      gap: 10px;
      width: 50%;
    }

    .text--compact {
      flex-direction: row;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .text--compact .btn-title {
      font-size: 0.95rem;
    }

    .text--compact .btn-subtitle {
      font-size: 0.9rem;
      margin-top: 0.2rem;
    }
  }

  @media (max-width: 767px) {
    .btn-call {
      gap: 10px;
      width: 100%;
      min-height: 50px;
    }

    .faq-footer-btn {
      min-height: 50px;
      padding: 12px 18px;
      font-size: 0.95rem;
    }

    .pill-instant-answer {
      margin-top: 0;
    }
  }
</style>
